import React, {FC, useEffect, useState} from "react";
import {Button, Card, Col, Descriptions, List, Row, Tag, Tooltip} from "antd";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import {
    CheckCircleOutlined,
    EnvironmentOutlined,
    FileTextOutlined,
    CalendarOutlined,
    ShoppingOutlined,
    GlobalOutlined, LeftOutlined, BorderlessTableOutlined
} from "@ant-design/icons";
import moment from "moment";
import QuickDDSAssignment from "./QuickDDSAssignment";
import QuickLocationAssignment from "./QuickLocationAssignment";
import ButtonWithIcon from "../components/buttons/button-with-icon";

interface OrdersListProps {
    supplierIsEu?: boolean;
    isGlobalAdmin?: boolean;
}

const OrdersList: FC<OrdersListProps> = ({ supplierIsEu, isGlobalAdmin }) => {
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [orders, setOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [ddsDrawerVisible, setDdsDrawerVisible] = useState(false);
    const [locationDrawerVisible, setLocationDrawerVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = () => {
        if (axiosInstance.current != null) {
            setLoading(true);

            axiosInstance.current
                .get('/resource/pending-orders', { 
                    params: {
                        sort: 'orderDate,desc'
                    }
                })
                .then((response: any) => {
                    const data = response.data;
                    if (data.content) {
                        setOrders(data.content);
                    } else if (Array.isArray(data)) {
                        setOrders(data);
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const handleAssignDDS = (record: any) => {
        setSelectedOrder(record);
        setDdsDrawerVisible(true);
    };

    const handleAssignLocation = (record: any) => {
        setSelectedOrder(record);
        setLocationDrawerVisible(true);
    };

    const showDDSButton = isGlobalAdmin || supplierIsEu;
    const showLocationButton = isGlobalAdmin || !supplierIsEu;

    return (
            <>
                <div
                    id="scrollableDiv"
                    style={{
                        height: 400,
                        overflow: 'auto',
                        padding: '0 16px',
                        border: '1px solid rgba(140, 140, 140, 0.35)',
                    }}
                >
                    <List
                        loading={loading}
                        dataSource={orders}
                        renderItem={(item: any) => (
                            <List.Item
                                key={item.id}
                                style={{
                                    padding: 10
                                }}
                            >
                                <Row gutter={24}>
                                    <Col span={24}>
                                        <Descriptions title={
                                            <>{
                                                item.orderNo
                                            }</>
                                        } extra={
                                            <div>
                                                <Button
                                                    size="small"
                                                    onClick={() => handleAssignDDS(item)}
                                                >
                                                    <ButtonWithIcon icon={<BorderlessTableOutlined/>}
                                                                    text={<Trans>Assign DDS</Trans>}/>
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => handleAssignLocation(item)}
                                                >
                                                    <ButtonWithIcon icon={<GlobalOutlined/>}
                                                                    text={<Trans>Assign Location</Trans>}/>
                                                </Button>
                                            </div>
                                        }>
                                            <Descriptions.Item label="OrderNo">{item.orderNo}</Descriptions.Item>
                                            <Descriptions.Item
                                                label="OrderDate">{item.orderDate ? moment(item.orderDate).format('YYYY.MM.DD') : '-'}</Descriptions.Item>
                                            <Descriptions.Item
                                                label="DeliveryDate">{item.deliveryDate ? moment(item.deliveryDate).format('YYYY.MM.DD') : '-'}</Descriptions.Item>
                                            <Descriptions.Item
                                                label="ItemNo">{item.itemNo || (item.item && item.item.itemNo)}</Descriptions.Item>
                                            <Descriptions.Item label="Quantity">{item.orderQuantity}</Descriptions.Item>
                                            <Descriptions.Item label="Status">{
                                                <Tag
                                                    className="dash-tag"
                                                    color={item.status === 'COMPLETED' ? 'green' : item.status === 'ORDERED' ? 'blue' : 'orange'}
                                                >
                                                    {item.status}
                                                </Tag>
                                            }</Descriptions.Item>
                                        </Descriptions>
                                    </Col>
                                </Row>
                            </List.Item>
                        )}
                        locale={{
                            emptyText: <Trans>No orders found</Trans>
                        }}
                    />
                </div>
                <QuickDDSAssignment
                    visible={ddsDrawerVisible}
                    order={selectedOrder}
                    onClose={() => setDdsDrawerVisible(false)}
                    onSuccess={() => { fetchOrders(); }}
            />
            <QuickLocationAssignment 
                visible={locationDrawerVisible}
                order={selectedOrder}
                onClose={() => setLocationDrawerVisible(false)}
                onSuccess={() => { fetchOrders(); }}
            />
            </>
    );
};

export default OrdersList;
